import { create } from "zustand";

import type {
  CreateParticipantRequest,
  UpdateParticipantRequest,
} from "src/core/backend/clients/backendClient";
import { backendService } from "src/core/backend/services/backendService";
import { logService } from "src/core/services/logService";
import type { ParticipantModel } from "../models/participantModel";
import { participantService } from "./participantService";

interface ParticipantState {
  readonly loading: boolean;
  readonly participants: ParticipantModel[];

  clear: () => void;

  participantList: () => Promise<void>;
  participantPost: (request: CreateParticipantRequest) => Promise<void>;
  participantPut: (request: UpdateParticipantRequest) => Promise<void>;
  participantDelete: (id: number) => Promise<void>;
}

export const useParticipantStore = create<ParticipantState>()((set) => ({
  loading: false,
  participants: [],

  clear: () => {
    set({
      loading: false,
      participants: [],
    });
  },

  participantList: async () => {
    set({ loading: true });

    const response = await participantService
      .participantList()
      .then((response) => {
        if (logService.globalLog) logService.info("participantList", response);
        return response;
      })
      .catch((error) => {
        logService.error("participantList", error);
        set({ loading: false });
        throw error;
      });

    const participants = response.map((item) => {
      const participant: ParticipantModel = {
        ...item,
        id: item.id ?? 0,
        userId: item.userId ?? 0,
        name: item.name ?? "",
        isLoading: false,
      };
      backendService.personResponseFix(participant);
      return participant;
    });

    set({ participants: participants, loading: false });
  },

  participantPost: async (request: CreateParticipantRequest) => {
    set({ loading: true });

    backendService.nullToUndefined(request);
    backendService.personRequestFix(request);

    await participantService
      .participantPost(request)
      .then(() => {
        if (logService.globalLog) logService.info("participantPost", request);
      })
      .catch((error) => {
        logService.error("participantPost", error);
        set({ loading: false });
        throw error;
      });

    set({ loading: false });
  },

  participantPut: async (request: UpdateParticipantRequest) => {
    set({ loading: true });

    backendService.nullToUndefined(request);
    backendService.personRequestFix(request);

    await participantService
      .participantPut(request)
      .then(() => {
        if (logService.globalLog) logService.info("participantPut", request);
      })
      .catch((error) => {
        logService.error("participantPut", error);
        set({ loading: false });
        throw error;
      });

    set({ loading: false });
  },

  participantDelete: async (id: number) => {
    set({ loading: true });

    await participantService
      .participantDelete(id)
      .then(() => {
        if (logService.globalLog) logService.info("participantDelete", id);
      })
      .catch((error) => {
        logService.error("participantDelete", error);
        set({ loading: false });
        throw error;
      });

    set({ loading: false });
  },
}));
